import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FileText, Settings, LogOut, ArrowRight } from 'lucide-react';
import { selectCurrentUser, logoutUser } from '../store/slices/authSlice';

/**
 * AccountPage — landing page after sign-in.
 *
 * Design system anchors:
 *   - Container width: --container-default (1200px) — standard inner page
 *   - Cormorant H1, eyebrow above
 *   - Card surfaces use --shadow-0 (flat) with hairline border
 *   - Tile actions are secondary buttons (outlined charcoal)
 */
export default function AccountPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(selectCurrentUser);

  const handleLogout = async () => {
    await dispatch(logoutUser());
    navigate('/');
  };

  if (!user) return null;

  const initials = (user.name || user.email)
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <main className="min-h-screen bg-cream-100 pt-24 md:pt-32 pb-24">
      <div className="page-container max-w-5xl">
        <p className="section-label mb-3">My account</p>
        <h1 className="font-serif text-4xl md:text-5xl text-charcoal leading-tight tracking-tight">
          {user.name?.split(' ')[0] ? `Welcome back, ${user.name.split(' ')[0]}` : 'Welcome back'}
        </h1>

        {/* Profile card */}
        <div className="bg-white border border-gray-100 mt-10 p-6 md:p-8 flex items-center gap-5">
          {user.avatar ? (
            <img
              src={user.avatar}
              alt=""
              className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover"
            />
          ) : (
            <span
              className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-gold text-charcoal flex items-center justify-center font-sans font-semibold text-2xl"
              aria-hidden="true"
            >
              {initials}
            </span>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-serif text-2xl text-charcoal truncate">{user.name}</p>
            <p className="font-sans text-sm text-warm-stone truncate">{user.email}</p>
            {user.company && (
              <p className="font-sans text-xs text-warm-stone tracking-widest uppercase mt-1">
                {user.company}
              </p>
            )}
          </div>
        </div>

        {/* Action tiles */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
          <Tile
            to="/account/quotes"
            icon={<FileText size={18} />}
            title="My Quote Requests"
            body="Review submitted RFQs and the responses from the sales team."
          />
          <Tile
            to="/account/settings"
            icon={<Settings size={18} />}
            title="Account Settings"
            body="Update your name, contact info, and password."
          />
          <button
            onClick={handleLogout}
            className="text-left bg-white border border-gray-100 p-6 hover:border-charcoal transition-colors group"
          >
            <span className="flex items-center justify-between mb-3 text-charcoal">
              <LogOut size={18} />
              <ArrowRight
                size={14}
                className="text-warm-stone group-hover:text-gold transition-colors"
              />
            </span>
            <p className="font-serif text-xl text-charcoal">Sign out</p>
            <p className="font-sans text-sm text-warm-stone mt-1.5">
              End your session on this device.
            </p>
          </button>
        </div>
      </div>
    </main>
  );
}

function Tile({ to, icon, title, body }) {
  return (
    <Link
      to={to}
      className="block bg-white border border-gray-100 p-6 hover:border-charcoal transition-colors group"
    >
      <span className="flex items-center justify-between mb-3 text-charcoal">
        {icon}
        <ArrowRight
          size={14}
          className="text-warm-stone group-hover:text-gold transition-colors"
        />
      </span>
      <p className="font-serif text-xl text-charcoal">{title}</p>
      <p className="font-sans text-sm text-warm-stone mt-1.5">{body}</p>
    </Link>
  );
}
